import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { guestsApi } from '../services/api';
import GuestDetailDialog from '../components/GuestDetailDialog';

interface Guest {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  loyalty_points: number;
  created_at: string;
}

const Guests: React.FC = () => {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [filteredGuests, setFilteredGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [selectedGuestId, setSelectedGuestId] = useState<number | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchGuests();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = guests.filter(
        (guest) =>
          guest.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guest.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guest.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          guest.phone?.includes(searchTerm)
      );
      setFilteredGuests(filtered);
    } else {
      setFilteredGuests(guests);
    }
  }, [searchTerm, guests]);

  const fetchGuests = async () => {
    try {
      setLoading(true);
      const response = await guestsApi.getAll();
      const guestsList = response.data.data || [];
      setGuests(guestsList);
      setFilteredGuests(guestsList);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load guests');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (guest: Guest) => {
    setEditingGuest(guest);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGuest(null);
  };

  const handleSave = async () => {
    try {
      const formData = new FormData(document.getElementById('guest-form') as HTMLFormElement);
      const guestData = {
        first_name: formData.get('first_name'),
        last_name: formData.get('last_name'),
        email: formData.get('email') || null,
        phone: formData.get('phone') || null,
      };

      if (editingGuest) {
        await guestsApi.update(editingGuest.id, guestData);
      } else {
        await guestsApi.create(guestData);
      }
      handleCloseDialog();
      fetchGuests();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save guest');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Guests Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Guest
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Guests</Typography>
              <Typography variant="h4">{guests.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Loyalty Members</Typography>
              <Typography variant="h4" color="primary.main">
                {guests.filter(g => g.loyalty_points > 0).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search guests by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Loyalty Points</TableCell>
              <TableCell>Member Since</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGuests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    {searchTerm ? 'No guests found matching your search.' : 'No guests found. Add your first guest to get started.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredGuests.map((guest) => (
                <TableRow key={guest.id} hover>
                  <TableCell>
                    {guest.first_name} {guest.last_name}
                  </TableCell>
                  <TableCell>{guest.email || '-'}</TableCell>
                  <TableCell>{guest.phone || '-'}</TableCell>
                  <TableCell>{guest.loyalty_points}</TableCell>
                  <TableCell>
                    {new Date(guest.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedGuestId(guest.id);
                        setDetailDialogOpen(true);
                      }}
                      title="View Details"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this guest?')) {
                          try {
                            await guestsApi.delete(guest.id);
                            fetchGuests();
                          } catch (err: any) {
                            setError(err.response?.data?.message || 'Failed to delete guest');
                          }
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGuest ? 'Edit Guest' : 'Add New Guest'}</DialogTitle>
        <form id="guest-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <DialogContent>
            <TextField
              fullWidth
              name="first_name"
              label="First Name"
              margin="normal"
              defaultValue={editingGuest?.first_name || ''}
              required
            />
            <TextField
              fullWidth
              name="last_name"
              label="Last Name"
              margin="normal"
              defaultValue={editingGuest?.last_name || ''}
              required
            />
            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              margin="normal"
              defaultValue={editingGuest?.email || ''}
            />
            <TextField
              fullWidth
              name="phone"
              label="Phone"
              margin="normal"
              defaultValue={editingGuest?.phone || ''}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <GuestDetailDialog
        open={detailDialogOpen}
        guestId={selectedGuestId}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedGuestId(null);
        }}
        onSuccess={() => {
          fetchGuests();
        }}
      />
    </Box>
  );
};

export default Guests;

